import type { MouseEventHandler } from 'react';
import { IconButton } from '../Button/IconButton';
import type { BoxProps } from '../Core';
import { FormControlSizes } from '../Form/types';
import { Icon, type IconProps } from '../Icons/Icon';
import { IconName } from '../Icons/IconNames';
import { AlertWrapper } from './styles';
import { AlertVariants } from './types';

export { AlertVariants };
export interface AlertProps extends BoxProps {
  dismissable?: boolean;
  onDismiss?: MouseEventHandler<HTMLButtonElement>;
  size?: FormControlSizes;
  variant?: AlertVariants;
  isBordered?: boolean;
  showIcon?: boolean;
  /** When true, suppresses the Negative variant's red tint — opt-in for surfaces where it hurts text contrast. */
  flatNegative?: boolean;
}

const MARGINS = {
  margin: '-4px',
  marginLeft: 'auto',
};

export const Alert = ({
  variant = AlertVariants.Default,
  onDismiss,
  dismissable = true,
  children,
  isBordered = true,
  showIcon = true,
  alignItems = 'flex-start',
  flatNegative = false,
  ...props
}: AlertProps) => {
  const iconProps = getAlertIconProps(variant);

  return (
    <AlertWrapper
      textAlign="left"
      isBordered={isBordered}
      alignItems={alignItems}
      variant={variant}
      flatNegative={flatNegative}
      {...props}
    >
      {showIcon && <Icon {...iconProps} />}
      {children}
      {dismissable && (
        <IconButton
          onClick={onDismiss}
          icon={IconName.Close}
          color="gray.100"
          size={FormControlSizes.Small}
          style={MARGINS}
          ghost
        />
      )}
    </AlertWrapper>
  );
};

const getAlertIconProps = (variant: AlertVariants): Pick<IconProps, 'icon'> & { color: string } => {
  switch (variant) {
    case AlertVariants.Warning:
      return { icon: IconName.ExclamationSolid, color: 'colors.yellow.lighten' };
    case AlertVariants.Negative:
      return { icon: IconName.ExclamationSolid, color: 'colors.red.lighten' };
    case AlertVariants.Notice:
      return { icon: IconName.ExclamationSolid, color: 'colors.gray[090]' };
    case AlertVariants.Positive:
      return { icon: IconName.CheckCircleSolid, color: 'colors.green.lighten' };
    case AlertVariants.Guide:
      return { icon: IconName.LightBulb, color: 'colors.blue.lighten' };
    case AlertVariants.Info:
    default:
      return { icon: IconName.InformationCircleSolid, color: 'colors.gray[090]' };
  }
};
